import {
  Box,
  Typography,
  Avatar,
  Divider,
  Button,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Slider,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";

import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import axios from "axios";

import CloseIcon from "@mui/icons-material/Close";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import CropIcon from "@mui/icons-material/Crop";

import { useAuth } from "../../auth/useAuth";

import { APP_API_V1_BASE } from "../../config/api";
// ─── Design Tokens ────────────────────────────────────────────────
const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'DM Sans', 'Helvetica Neue', sans-serif";

const C = {
  bg: "radial-gradient(circle at 20% 20%, rgba(59,130,246,0.12), transparent 40%), radial-gradient(circle at 80% 0%, rgba(139,92,246,0.10), transparent 35%), linear-gradient(180deg, #F8FAFF 0%, #EEF2FF 100%)",

  surface: "rgba(255,255,255,0.75)",
  surfaceAlt: "rgba(248,250,252,0.9)",
  border: "rgba(15,23,42,0.08)",

  ink: "#0f172a",
  inkMuted: "#64748b",
  inkFaint: "#94a3b8",

  accent: "#4f46e5", // 🔵 Indigo (modern)
  accentLight: "#eef2ff",
  accentSoft: "#c7d2fe",

  success: "#16a34a",
  successLight: "#f0fdf4",

  dark: "#020617",
};

// ─── Country Codes ────────────────────────────────────────────────
const COUNTRY_CODES = [
  { code: "IN", label: "India", dial: "+91", flag: "🇮🇳" },
  { code: "US", label: "United States", dial: "+1", flag: "🇺🇸" },
  { code: "GB", label: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { code: "AE", label: "UAE", dial: "+971", flag: "🇦🇪" },
  { code: "SG", label: "Singapore", dial: "+65", flag: "🇸🇬" },
  { code: "AU", label: "Australia", dial: "+61", flag: "🇦🇺" },
  { code: "CA", label: "Canada", dial: "+1", flag: "🇨🇦" },
  { code: "DE", label: "Germany", dial: "+49", flag: "🇩🇪" },
  { code: "FR", label: "France", dial: "+33", flag: "🇫🇷" },
  { code: "JP", label: "Japan", dial: "+81", flag: "🇯🇵" },
  { code: "CN", label: "China", dial: "+86", flag: "🇨🇳" },
  { code: "BR", label: "Brazil", dial: "+55", flag: "🇧🇷" },
  { code: "ZA", label: "South Africa", dial: "+27", flag: "🇿🇦" },
  { code: "NG", label: "Nigeria", dial: "+234", flag: "🇳🇬" },
  { code: "PK", label: "Pakistan", dial: "+92", flag: "🇵🇰" },
  { code: "BD", label: "Bangladesh", dial: "+880", flag: "🇧🇩" },
  { code: "NP", label: "Nepal", dial: "+977", flag: "🇳🇵" },
  { code: "LK", label: "Sri Lanka", dial: "+94", flag: "🇱🇰" },
  { code: "MY", label: "Malaysia", dial: "+60", flag: "🇲🇾" },
  { code: "ID", label: "Indonesia", dial: "+62", flag: "🇮🇩" },
];

// ─── inputSx ─────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: FONT_BODY,
    fontSize: "14px",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.inkFaint },
    "&.Mui-focused fieldset": { borderColor: C.accent },
  },
  "& .MuiInputLabel-root": { fontFamily: FONT_BODY, fontSize: "13.5px" },
  "& .MuiInputLabel-root.Mui-focused": { color: C.accent },
};

// ─── Canvas crop helper ───────────────────────────────────────────
/**
 * Given an image URL and a pixelCrop rect (from react-easy-crop),
 * draws the cropped region onto a canvas and returns a Blob + object URL.
 */
async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Account for rotation by rendering onto a rotated intermediate canvas first
  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);
  ctx.drawImage(
    image,
    safeArea / 2 - image.width / 2,
    safeArea / 2 - image.height / 2,
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // Resize canvas to the final crop dimensions
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y),
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve({
          blob,
          url: URL.createObjectURL(blob),
        });
      },
      "image/jpeg",
      0.92,
    );
  });
}

// ─── ImageCropDialog ─────────────────────────────────────────────
/**
 * Standalone crop dialog.
 * Props:
 *   open        – boolean
 *   imageSrc    – data URL / object URL of the raw selected image
 *   onCancel    – () => void
 *   onConfirm   – ({ blob, url }) => void
 */
function ImageCropDialog({ open, imageSrc, onCancel, onConfirm }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);

  // Reset state whenever a new image is opened
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setApplying(true);
    try {
      const result = await getCroppedBlob(
        imageSrc,
        croppedAreaPixels,
        rotation,
      );
      onConfirm(result);
    } catch (e) {
      console.error("Crop failed", e);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          fontFamily: FONT_BODY,
          m: isMobile ? 0 : 2,
          overflow: "hidden",
          background: C.dark,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: 1.5,
          background: C.dark,
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <CropIcon sx={{ fontSize: 18, color: C.accentSoft }} />
          <Box>
            <Typography
              sx={{
                fontFamily: FONT_DISPLAY,
                fontSize: { xs: "17px", sm: "19px" },
                fontWeight: 700,
                color: "#fff",
              }}
            >
              Crop Photo
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontSize: "11.5px",
                color: "rgba(255,255,255,0.45)",
              }}
            >
              Drag to reposition · pinch or scroll to zoom
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{
            backgroundColor: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      {/* Crop canvas area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "55vw", sm: "340px" },
          minHeight: 240,
          background: "#000",
        }}
      >
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round" // circular crop for avatars
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { background: "#000" },
              cropAreaStyle: {
                border: `2px solid ${C.accentSoft}`,
                boxShadow: `0 0 0 9999px rgba(0,0,0,0.62)`,
              },
            }}
          />
        )}
      </Box>

      {/* Controls */}
      <DialogContent
        sx={{
          background: C.dark,
          px: { xs: 2.5, sm: 3.5 },
          pt: 2.5,
          pb: 1,
        }}
      >
        {/* Zoom slider */}
        <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
          <ZoomInIcon
            sx={{ fontSize: 18, color: "rgba(255,255,255,0.4)", flexShrink: 0 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontSize: "10.5px",
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.35)",
                mb: 0.8,
              }}
            >
              Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              onChange={(_, v) => setZoom(v)}
              sx={{
                color: C.accent,
                height: 4,
                "& .MuiSlider-thumb": {
                  width: 16,
                  height: 16,
                  backgroundColor: C.accentSoft,
                  "&:hover, &.Mui-focusVisible": {
                    boxShadow: `0 0 0 6px ${C.accent}33`,
                  },
                },
                "& .MuiSlider-rail": {
                  backgroundColor: "rgba(255,255,255,0.15)",
                },
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontSize: "12px",
              color: "rgba(255,255,255,0.4)",
              minWidth: 30,
            }}
          >
            {zoom.toFixed(1)}×
          </Typography>
        </Stack>

        {/* Rotation slider */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <RotateRightIcon
            sx={{ fontSize: 18, color: "rgba(255,255,255,0.4)", flexShrink: 0 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontSize: "10.5px",
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.35)",
                mb: 0.8,
              }}
            >
              Rotation
            </Typography>
            <Slider
              value={rotation}
              min={-180}
              max={180}
              step={1}
              onChange={(_, v) => setRotation(v)}
              sx={{
                color: C.accent,
                height: 4,
                "& .MuiSlider-thumb": {
                  width: 16,
                  height: 16,
                  backgroundColor: C.accentSoft,
                  "&:hover, &.Mui-focusVisible": {
                    boxShadow: `0 0 0 6px ${C.accent}33`,
                  },
                },
                "& .MuiSlider-rail": {
                  backgroundColor: "rgba(255,255,255,0.15)",
                },
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontSize: "12px",
              color: "rgba(255,255,255,0.4)",
              minWidth: 30,
            }}
          >
            {rotation}°
          </Typography>
        </Stack>
      </DialogContent>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mt: 1 }} />

      {/* Actions */}
      <DialogActions
        sx={{
          background: C.dark,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          fullWidth={useMediaQuery(useTheme().breakpoints.down("sm"))}
          onClick={onCancel}
          sx={{
            fontFamily: FONT_BODY,
            fontSize: "13px",
            fontWeight: 500,
            textTransform: "none",
            color: "rgba(255,255,255,0.5)",
            borderRadius: "999px",
            px: 2,
            border: "1px solid rgba(255,255,255,0.12)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
          }}
        >
          Cancel
        </Button>
        <Button
          fullWidth={useMediaQuery(useTheme().breakpoints.down("sm"))}
          variant="contained"
          onClick={handleConfirm}
          disabled={applying}
          sx={{
            fontFamily: FONT_BODY,
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "999px",
            px: 3,
            py: 1,
            backgroundColor: C.accent,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#9A3412", boxShadow: "none" },
          }}
        >
          {applying ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            "Crop & Use"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── InfoRow ──────────────────────────────────────────────────────
function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box
        sx={{
          mt: "2px",
          width: 34,
          height: 34,
          minWidth: 34,
          borderRadius: "9px",
          backgroundColor: C.accentLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: "10.5px",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: C.inkFaint,
            mb: 0.4,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: "14.5px",
            color: C.ink,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

// ─── RoleBadge ───────────────────────────────────────────────────
function RoleBadge({ role }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.5,
        py: 0.5,
        borderRadius: "999px",
        backgroundColor: C.accentLight,
        border: `1px solid ${C.accentSoft}`,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: C.accent,
        }}
      />
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: "11.5px",
          fontWeight: 600,
          color: C.accent,
          letterSpacing: "0.04em",
          textTransform: "capitalize",
        }}
      >
        {role}
      </Typography>
    </Box>
  );
}

// ─── IndustryLabel ───────────────────────────────────────────────
function IndustryLabel({ industry }) {
  if (!industry) return null;
  return (
    <Box
      sx={{
        mt: 0.8,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.8,
        px: 1.5,
        py: 0.45,
        borderRadius: "8px",
        backgroundColor: C.surfaceAlt,
        border: `1px solid ${C.border}`,
      }}
    >
      <BusinessOutlinedIcon sx={{ fontSize: 12, color: C.inkFaint }} />
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: "12px",
          fontWeight: 500,
          color: C.inkMuted,
        }}
      >
        {industry}
      </Typography>
    </Box>
  );
}

// ─── OrgRow ───────────────────────────────────────────────────────
function OrgRow({ org, isActive, onSwitch }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 1.5, sm: 0 },
        px: { xs: 2, sm: 2.5 },
        py: 2,
        backgroundColor: isActive ? C.accentLight : "transparent",
        borderLeft: isActive
          ? `3px solid ${C.accent}`
          : "3px solid transparent",
        transition: "background 0.15s",
        "&:hover": { backgroundColor: isActive ? C.accentLight : C.surfaceAlt },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ minWidth: 0, flex: 1 }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: "10px",
            backgroundColor: isActive ? C.accent : C.surfaceAlt,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BusinessOutlinedIcon
            sx={{ fontSize: 17, color: isActive ? "#fff" : C.inkMuted }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontSize: "14px",
              fontWeight: 600,
              color: C.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {org.name}
          </Typography>
          <Typography
            sx={{ fontFamily: FONT_BODY, fontSize: "12px", color: C.inkMuted }}
          >
            {org.role}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ ml: { xs: "52px", sm: 0 } }}
      >
        <Box
          // sx={{
          //   display: "inline-flex",
          //   alignItems: "center",
          //   gap: 0.5,
          //   px: 1.2,
          //   py: 0.35,
          //   borderRadius: "999px",
          //   background:
          //     org.status === "active" ? C.success : C.surfaceAlt,
          //   border: `1px solid ${org.status === "active" ? C.success : C.border}`,
          // }}
        >
          {org.status === "active" && (
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                backgroundColor: C.success,
              }}
            />
          )}
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontSize: "11px",
              fontWeight: 600,
              color: org.status === "active" ? C.success : C.inkMuted,
              textTransform: "capitalize",
            }}
          >
            {org.status}
          </Typography>
        </Box>

        {isActive ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.2,
              py: 0.35,
              borderRadius: "999px",
              backgroundColor: C.accentLight,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 13, color: C.success }} />
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontSize: "11px",
                fontWeight: 600,
                color: C.success,
              }}
            >
              Active
            </Typography>
          </Box>
        ) : (
          <Button
            size="small"
            onClick={onSwitch}
            startIcon={<SwapHorizIcon sx={{ fontSize: "14px !important" }} />}
            sx={{
              fontFamily: FONT_BODY,
              fontSize: "11.5px",
              fontWeight: 600,
              color: C.inkMuted,
              border: `1px solid ${C.border}`,
              borderRadius: "999px",
              px: 1.5,
              py: 0.4,
              textTransform: "none",
              "&:hover": {
                backgroundColor: C.surfaceAlt,
                color: C.ink,
                borderColor: C.inkFaint,
              },
            }}
          >
            Switch
          </Button>
        )}
      </Stack>
    </Box>
  );
}

// ─── CountryCodeSelect ────────────────────────────────────────────
function CountryCodeSelect({ value, onChange }) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      variant="standard" // ✅ important for inline look
      disableUnderline
      sx={{
        fontFamily: FONT_BODY,
        fontSize: "13px",
        minWidth: 70,
        mr: 1,
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          gap: "4px",
          p: 0,
        },
      }}
      renderValue={(val) => {
        const match = COUNTRY_CODES.find((c) => c.dial === val);
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography fontSize="14px">{match?.flag}</Typography>
            <Typography fontSize="13px" color={C.inkMuted}>
              {val}
            </Typography>
          </Stack>
        );
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: "12px",
            border: `1px solid ${C.border}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            maxHeight: 280,
          },
        },
      }}
    >
      {COUNTRY_CODES.map((c) => (
        <MenuItem key={c.code} value={c.dial}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontSize="14px">{c.flag}</Typography>
            <Typography fontSize="13px">{c.dial}</Typography>
            <Typography fontSize="13px" color={C.inkMuted}>
              {c.label}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
}

// ─── Main Component ───────────────────────────────────────────────
export default function ProfilePage() {
  const { user, organizations, activeOrg, switchOrg } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(user);

  // Crop dialog state
  const [cropOpen, setCropOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null); // data URL fed to cropper
  const [croppedPreview, setCroppedPreview] = useState(null); // object URL after crop
  const [croppedBlob, setCroppedBlob] = useState(null); // Blob to upload

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    countryCode: user?.country_code || "+91",
    removeImage: false,
  });

  useEffect(() => {
    const loadImage = async () => {
      if (!user) return;
      let u = { ...user };
      if (user.profile_image_url)
        u.profile_image_url = await getProfileImage(user.profile_image_url);
      setProfile(u);
    };
    loadImage();
  }, [user]);

  if (!user) return null;

  const initials = profile?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCountryCodeChange = (dial) =>
    setFormData((prev) => ({ ...prev, countryCode: dial }));

  // ── Image selection: validate → read as data URL → open crop dialog ──
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    // Reset input so same file can be re-selected after cancel
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file (JPG, PNG, WebP).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("Image must be less than 10 MB.");
      return;
    }
    // Read into data URL for the cropper (avoids blob URL revocation issues)
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  // ── Crop confirmed: store blob + preview URL ──
  const handleCropConfirm = ({ blob, url }) => {
    // Revoke previous preview to avoid memory leak
    if (croppedPreview) URL.revokeObjectURL(croppedPreview);
    setCroppedBlob(blob);
    setCroppedPreview(url);
    setFormData((prev) => ({ ...prev, removeImage: false }));
    setCropOpen(false);
    setRawImageSrc(null);
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    setRawImageSrc(null);
  };

  const handleRemoveImage = () => {
    if (croppedPreview) URL.revokeObjectURL(croppedPreview);
    setCroppedPreview(null);
    setCroppedBlob(null);
    setFormData((prev) => ({ ...prev, removeImage: true }));
  };

  const getProfileImage = async (blobUrl) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return blobUrl;
      const res = await axios.get(`${APP_API_V1_BASE}/profile/image-token`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      });
      return `${blobUrl}?${res.data.token}`;
    } catch {
      return blobUrl;
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert("Name cannot be empty");
      return;
    }
    if (!/^\d{6,15}$/.test(formData.phone)) {
      alert("Please enter a valid phone number (6–15 digits)");
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("User not authenticated");
        return;
      }

      const fd = new FormData();
      fd.append("name", formData.name.trim());
      fd.append("phone", formData.phone);
      fd.append("country_code", formData.countryCode);
      if (croppedBlob) fd.append("image", croppedBlob, "avatar.jpg");
      if (formData.removeImage) fd.append("remove_image", "true");

      const response = await axios.post(`${APP_API_V1_BASE}/profile`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      });

      let updatedUser = response.data.user;
      if (updatedUser.profile_image_url)
        updatedUser.profile_image_url = await getProfileImage(
          updatedUser.profile_image_url,
        );

      setProfile(updatedUser);
      setEditOpen(false);
      setSuccessOpen(true);

      // Clean up
      if (croppedPreview) URL.revokeObjectURL(croppedPreview);
      setCroppedPreview(null);
      setCroppedBlob(null);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = () => {
    setFormData({
      name: profile?.name || "",
      email: profile?.email || "",
      phone: profile?.phone || "",
      countryCode: profile?.country_code || "+91",
      removeImage: false,
    });
    setCroppedPreview(null);
    setCroppedBlob(null);
    setEditOpen(true);
  };

  const displayPhone = profile?.phone
    ? `${profile.country_code || formData.countryCode} ${profile.phone}`
    : null;

  // The avatar src shown in the edit dialog preview
  const dialogAvatarSrc =
    croppedPreview ||
    (formData.removeImage ? "" : profile?.profile_image_url) ||
    "";

  return (
    <Box
      sx={{
        fontFamily: FONT_BODY,
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
        minHeight: "100vh",
        px: { xs: 1.5, sm: 2, md: 4 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Box sx={{ maxWidth: 780, mx: "auto" }}>
        {/* ── Page Header ─────────────────────────────────────── */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            sx={{
              fontFamily: FONT_DISPLAY,
              fontSize: { xs: "22px", sm: "26px", md: "32px" },
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            My Profile
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontSize: { xs: "13px", md: "14px" },
              color: C.inkMuted,
              mt: 0.5,
            }}
          >
            Manage your personal information and organization access.
          </Typography>
        </Box>

        {/* ── Identity Card ────────────────────────────────────── */}
        <Box
          sx={{
            background: C.surface,
            backdropFilter: "blur(12px)",
            border: `1px solid ${C.border}`,
            borderRadius: "18px",
            boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
            transition: "all 0.3s ease",
            boxShadow: "0 20px 50px rgba(15,23,42,0.12)",
          }}
        >
          <Box
            sx={{
              height: { xs: 70, md: 90 },
              borderRadius: 1,
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(circle at 20% 60%, rgba(255,255,255,0.08) 0%, transparent 60%)",
              },
            }}
          />

          <Box sx={{ px: { xs: 2, md: 3.5 }, pb: { xs: 2.5, md: 3.5 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              sx={{ mt: { xs: "-28px", md: "-38px" }, mb: 2.5 }}
            >
              <Avatar
                src={profile?.profile_image_url || ""}
                sx={{
                  width: { xs: 60, md: 78 },
                  height: { xs: 60, md: 78 },
                  fontSize: { xs: "17px", md: "22px" },
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  backgroundColor: C.accentSoft,
                  color: C.accent,
                  border: `3px solid ${C.surface}`,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
                }}
              >
                {!profile?.profile_image_url && initials}
              </Avatar>

              <Button
                variant="outlined"
                size="small"
                startIcon={
                  <EditOutlinedIcon sx={{ fontSize: "14px !important" }} />
                }
                onClick={openEditDialog}
                sx={{
                  fontFamily: FONT_BODY,
                  fontSize: { xs: "11px", md: "12.5px" },
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "999px",
                  px: { xs: 1.2, md: 2 },
                  py: 0.6,
                  color: C.ink,
                  borderColor: C.border,
                  "&:hover": {
                    backgroundColor: C.surfaceAlt,
                    borderColor: C.inkFaint,
                  },
                }}
              >
                Edit Profile
              </Button>
            </Stack>

            <Box sx={{ mb: 2.5 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: { xs: "18px", md: "22px" },
                    fontWeight: 700,
                    color: C.ink,
                    letterSpacing: "-0.01em",
                    wordBreak: "break-word",
                  }}
                >
                  {profile?.name}
                </Typography>
                {profile?.global_role && (
                  <RoleBadge role={profile.global_role} />
                )}
              </Stack>
              <IndustryLabel industry={profile?.industry} />
            </Box>

            <Divider sx={{ borderColor: C.border, mb: 2.5 }} />

            <Stack spacing={2.5}>
              <InfoRow
                icon={
                  <EmailOutlinedIcon sx={{ fontSize: 16, color: C.accent }} />
                }
                label="Email Address"
                value={profile?.email}
              />
              <InfoRow
                icon={
                  <PhoneOutlinedIcon sx={{ fontSize: 16, color: C.accent }} />
                }
                label="Phone Number"
                value={displayPhone}
              />
              <InfoRow
                icon={
                  <BadgeOutlinedIcon sx={{ fontSize: 16, color: C.accent }} />
                }
                label="Global Role"
                value={profile?.global_role}
              />
            </Stack>
          </Box>
        </Box>

        {/* ── Organizations ────────────────────────────────────── */}
        <Box
          sx={{
            background: C.surface,
            backdropFilter: "blur(12px)",
            border: `1px solid ${C.border}`,
            borderRadius: "18px",
            boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
            transition: "all 0.3s ease",
            mt: 2,
            boxShadow: "0 20px 50px rgba(15,23,42,0.12)",
          }}
        >
          <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: 700,
                  color: C.ink,
                }}
              >
                Organizations
              </Typography>
              <Box
                sx={{
                  px: 1.2,
                  py: 0.2,
                  borderRadius: "999px",
                  backgroundColor: C.surfaceAlt,
                  border: `1px solid ${C.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_BODY,
                    fontSize: "11px",
                    fontWeight: 700,
                    color: C.inkMuted,
                  }}
                >
                  {organizations.length}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: C.border }} />

          {organizations.length === 0 ? (
            <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
              <BusinessOutlinedIcon
                sx={{ fontSize: 32, color: C.border, mb: 1 }}
              />
              <Typography
                sx={{
                  fontFamily: FONT_BODY,
                  fontSize: "14px",
                  color: C.inkMuted,
                }}
              >
                You're not part of any organization yet.
              </Typography>
            </Box>
          ) : (
            organizations.map((org, i) => (
              <Box key={org.id}>
                <OrgRow
                  org={org}
                  isActive={activeOrg?.id === org.id}
                  onSwitch={() => switchOrg(org)}
                />
                {i < organizations.length - 1 && (
                  <Divider sx={{ borderColor: C.border, mx: 2 }} />
                )}
              </Box>
            ))
          )}
        </Box>

        {/* ── Image Crop Dialog ────────────────────────────────── */}
        <ImageCropDialog
          open={cropOpen}
          imageSrc={rawImageSrc}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />

        {/* ── Edit Profile Dialog ──────────────────────────────── */}
        <Dialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : "20px",
              fontFamily: FONT_BODY,
              m: isMobile ? 0 : 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              pb: 1.5,
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: { xs: "18px", sm: "20px" },
                  fontWeight: 700,
                  color: "#fff",
                }}
              >
                Edit Profile
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_BODY,
                  fontSize: "12.5px",
                  color: "#ffffff",
                }}
              >
                Update your personal information
              </Typography>
            </Box>
            <IconButton
              onClick={() => setEditOpen(false)}
              size="small"
              sx={{
                backgroundColor: C.surfaceAlt,
                "&:hover": { backgroundColor: "#fff" },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </DialogTitle>

          <Divider sx={{ borderColor: C.border }} />

          <DialogContent
            sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2.5, sm: 3 }, pb: 1 }}
          >
            {/* Hidden file input — triggers crop flow */}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              id="profile-upload"
              onChange={handleImageSelect}
            />

            {/* ── Photo preview + upload controls ── */}
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar
                  src={dialogAvatarSrc}
                  sx={{
                    width: { xs: 68, sm: 80 },
                    height: { xs: 68, sm: 80 },
                    fontFamily: FONT_DISPLAY,
                    fontWeight: 700,
                    fontSize: "20px",
                    backgroundColor: C.accentSoft,
                    color: C.accent,
                    border: `2px solid ${C.border}`,
                    // Subtle ring when a new crop is ready
                    ...(croppedPreview && {
                      border: `2.5px solid ${C.accent}`,
                      boxShadow: `0 0 0 3px ${C.accentLight}`,
                    }),
                  }}
                >
                  {!dialogAvatarSrc && initials}
                </Avatar>

                {/* Camera button — opens file picker */}
                <IconButton
                  component="label"
                  htmlFor="profile-upload"
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: -4,
                    right: -4,
                    backgroundColor: C.accent,
                    color: "#fff",
                    width: 28,
                    height: 28,
                    "&:hover": { backgroundColor: "#9A3412" },
                  }}
                >
                  <PhotoCamera sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_BODY,
                    fontSize: "13.5px",
                    fontWeight: 600,
                    color: C.ink,
                    mb: 0.4,
                  }}
                >
                  Profile Photo
                </Typography>

                {/* Status line — shows "Cropped & ready" when a crop exists */}
                {croppedPreview ? (
                  <Typography
                    sx={{
                      fontFamily: FONT_BODY,
                      fontSize: "12px",
                      color: C.success,
                      fontWeight: 600,
                      mb: 0.6,
                    }}
                  >
                    ✓ Cropped & ready to save
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: FONT_BODY,
                      fontSize: "12px",
                      color: C.inkFaint,
                      mb: 0.6,
                    }}
                  >
                    JPG, PNG or WebP · Max 10 MB
                  </Typography>
                )}

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Typography
                    component="label"
                    htmlFor="profile-upload"
                    sx={{
                      fontFamily: FONT_BODY,
                      fontSize: "12.5px",
                      color: C.accent,
                      cursor: "pointer",
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {croppedPreview ? "Change photo" : "Upload & crop"}
                  </Typography>

                  {(croppedPreview || profile?.profile_image_url) && (
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontSize: "12.5px",
                        color: C.inkFaint,
                        cursor: "pointer",
                        "&:hover": { color: "#DC2626" },
                      }}
                      onClick={handleRemoveImage}
                    >
                      Remove
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Full Name */}
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ mb: 2, ...inputSx }}
            />

            {/* Email (read-only) */}
            <TextField
              fullWidth
              label="Email"
              value={formData.email}
              disabled
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontFamily: FONT_BODY,
                  fontSize: "14px",
                  backgroundColor: C.surfaceAlt,
                },
                "& .MuiInputLabel-root": {
                  fontFamily: FONT_BODY,
                  fontSize: "13.5px",
                },
              }}
            />

            {/* Phone */}
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                setFormData((prev) => ({ ...prev, phone: val }));
              }}
              inputProps={{ maxLength: 15 }}
              placeholder="Enter phone number"
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CountryCodeSelect
                      value={formData.countryCode}
                      onChange={handleCountryCodeChange}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>

          <Divider sx={{ borderColor: C.border, mt: 2 }} />

          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: 1, sm: 0 },
            }}
          >
            <Button
              fullWidth={isMobile}
              onClick={() => setEditOpen(false)}
              sx={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                color: C.inkMuted,
                borderRadius: "999px",
                px: 2,
                ...(isMobile && { border: `1px solid ${C.border}` }),
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth={isMobile}
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              sx={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "999px",
                px: 3,
                py: 1,
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Success toast ─────────────────────────────────────── */}
        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setSuccessOpen(false)}
            sx={{
              fontFamily: FONT_BODY,
              borderRadius: "12px",
              backgroundColor: C.success,
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            }}
          >
            Profile updated successfully!
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
